<script setup lang="ts">
import { useStatsigEventsStore } from '@apps/main/new-desktop/core/stores';
import { PriceCalendar } from '@apps/main/new-desktop/modules/priceCalendar';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import {
  AppButton,
  AppFlicker,
  AppMessageBox,
  AppStandardModal,
  AppSvgIcon,
} from '@libs/ui';

import { usePeekPriceStore } from '../stores/peekPriceStore';

interface PeekPriceModalEmits {
  (eventName: 'cancel'): void;
  (eventName: 'confirm'): void;
}

const emit = defineEmits<PeekPriceModalEmits>();

const peekPriceStore = usePeekPriceStore();
const statsigEventsStore = useStatsigEventsStore();

function onSelectDate(date: Date): void {
  statsigEventsStore.logHotelSelectionPeekPriceModalDateSelected();
  statsigEventsStore.logCoreDateChanged({
    source: CoreEventSource.HotelSelection,
  });
  peekPriceStore.preselectDate(date);
}

function onSelectMonth(date: Date): void {
  statsigEventsStore.logHotelSelectionPeekPriceModalMonthSelected();
  peekPriceStore.selectMonth(date);
}
</script>

<template>
  <AppStandardModal
    footer-padding="small"
    @close="emit('cancel')"
    @confirm="emit('confirm')"
  >
    <template #body>
      <div class="peek-price-body">
        <AppMessageBox
          :text="$t('planner.calendar_shows_date_price')"
          message-type="brand-lagoon"
          class="message-box"
        >
          <template #left-icon>
            <AppSvgIcon name="lightbulb" />
          </template>
        </AppMessageBox>

        <div class="prices-for-hotel-text">
          {{ $t('planner.prices_in_calendar') }}
          <span class="hotel-name-text">{{ peekPriceStore.hotelName }}.</span>
        </div>

        <PriceCalendar
          v-if="peekPriceStore.priceCalendarState.anyResultsLoaded()"
          :selected-month="peekPriceStore.selectedMonth"
          :months="peekPriceStore.months"
          :price-calendar-state="peekPriceStore.priceCalendarState"
          :show-prices="true"
          :allow-hover-states="true"
          :show-header-padding="false"
          @select-month="onSelectMonth"
          @select-date="onSelectDate"
        />
        <AppFlicker
          v-else
          height="spacing-32"
          width="spacing-80"
          border-radius="border-radius-12"
          class="flicker"
        />
      </div>
    </template>

    <template #summary>
      <div class="peek-price-footer">
        <AppButton
          type="secondary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('cancel')"
        >
          {{ $t('planner.close') }}
        </AppButton>
        <AppButton
          type="primary"
          state="enabled"
          size="medium"
          :wide="true"
          @click="emit('confirm')"
        >
          {{ $t('planner.done') }}
        </AppButton>
      </div>
    </template>
  </AppStandardModal>
</template>

<style lang="scss" scoped>
.peek-price-body {
  width: 100%;
  height: 100%;

  display: grid;
  grid-template-rows: min-content min-content 1fr;

  .message-box {
    margin-bottom: var(--spacing-16);
  }

  .prices-for-hotel-text {
    @include font-label-m-light;
    color: var(--color-neutral-70);

    .hotel-name-text {
      @include font-label-m-bold;
      color: var(--color-neutral-70);
    }
  }

  .flicker {
    margin-top: var(--spacing-24);
  }
}

.peek-price-footer {
  width: 100%;
  display: flex;
  gap: var(--spacing-12);
}
</style>
